import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, ArrowRight } from 'lucide-react';
import { isAdminAuthenticated, loginAdmin } from '@/lib/adminAuth';

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAdminAuthenticated()) {
      navigate('/mtnadmin/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // Small delay for UX
    setTimeout(() => {
      if (loginAdmin(username.trim(), password)) {
        navigate('/mtnadmin/dashboard', { replace: true });
      } else {
        setError('Invalid credentials. Please try again.');
        setLoading(false);
      }
    }, 300);
  };

  return (
    <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-[#C9A961]/10 border border-[#C9A961]/30 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-6 h-6 text-[#C9A961]" />
          </div>
          <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-3">
            Private Access
          </span>
          <h1 className="font-serif text-3xl text-white font-light mb-2">Zera Admin</h1>
          <p className="font-sans text-xs text-white/40">
            Authorized personnel only. All access is logged.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white/[0.03] border border-white/10 backdrop-blur-sm p-8 space-y-6"
        >
          <div>
            <label className="font-sans text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">
              Username
            </label>
            <div className="relative">
              <User className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/10 pl-10 pr-4 py-3 font-sans text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C9A961] transition-colors"
                placeholder="Enter username"
                required
              />
            </div>
          </div>

          <div>
            <label className="font-sans text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/10 pl-10 pr-4 py-3 font-sans text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C9A961] transition-colors"
                placeholder="Enter password"
                required
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 px-4 py-3">
              <p className="font-sans text-xs text-red-300">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full font-sans text-[10px] tracking-[0.3em] uppercase px-8 py-4 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b8944d] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? 'Signing In...' : (
              <>
                Sign In <ArrowRight className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </form>

        <p className="text-center font-sans text-[10px] text-white/20 mt-8 tracking-wider">
          ZERA TRAVEL INTERNAL TOOLS
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
